import HttpStatus from 'components/Common/HttpStatus'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import config from 'constants/config'
import React from 'react'
import { connect } from 'react-redux'
import { Redirect, Switch } from 'react-router'
import Route from './TrackedRoute'
import loadable from 'components/utils/lazyImport'
import AuthRoute from './AuthRoute'
import Error404Page from 'components/Pages/Error404Page'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  BUSINESS_TRAVELLER_APPROVE_ORDER_ROUTE,
  BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE,
  BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE,
  BUSINESS_TRAVELLER_EMPLOYEE_SETUP_ROUTE,
  BUSINESS_TRAVELLER_EXPRESSION_OF_INTEREST_ROUTE,
  BUSINESS_TRAVELLER_FAQ_ROUTE,
  BUSINESS_TRAVELLER_LOGIN_ROUTE,
  BUSINESS_TRAVELLER_MY_APPROVALS_ROUTE,
  BUSINESS_TRAVELLER_MY_APPROVALS_VIEW_ROUTE,
  BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_ROUTE,
  BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_VIEW_ROUTE,
  BUSINESS_TRAVELLER_ROOT_ROUTE
} from 'components/App/Routes/routeConstants'
import { isLebtOrderSchemaV2Enabled } from 'businessTraveller/selectors/orderSchemaV2Selectors'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { isLebtSocieteEnabled } from 'businessTraveller/selectors/societeEnabledSelector'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'

const AccountDetailsPage = loadable(() => import(/* webpackChunkName: "AccountDetailsPage" */ 'components/Pages/AccountDetailsPage'))
const AccountDetailsTravellerPage = loadable(() => import(/* webpackChunkName: "AccountDetailsTravellerPage" */ 'components/Pages/AccountDetailsTravellerPage'))
const AccountPage = loadable(() => import(/* webpackChunkName: "AccountLandingPage" */ 'components/Pages/AccountPage/AccountLandingPage'))
const BedBankOfferPage = loadable(() => import(/* webpackChunkName: "OfferPage" */ 'components/Pages/BedbankOfferPage'))
const BestPricePage = loadable(() => import(/* webpackChunkName: "BestPricePage" */ 'components/Pages/BestPricePage'))
const CarHireSearchPage = loadable(() => import(/* webpackChunkName: "CarHireSearchPage" */ 'components/CarHire/CarHireSearchPage/CarHireSearchPage'))
const CheckoutGatewayPage = loadable(() => import(/* webpackChunkName: "CheckoutGatewayPage" */ 'checkout/Pages/GatewayPage'))
const CheckoutPageRoutes = loadable(() => import(/* webpackChunkName: "CheckoutPageRoutes" */ 'checkout/routes'))
const CheckoutRestoreCartPage = loadable(() => import(/* webpackChunkName: "CheckoutRestoreCartPage" */ 'checkout/Pages/RestoreCartPage'))
const ConfirmationPage = loadable(() => import(/* webpackChunkName: "ConfirmationPage" */ 'checkout/Components/Confirmation/ConfirmationPage'))
const ContentHub = loadable(() => import(/* webpackChunkName: "ContentHub" */ 'components/Pages/LandingPage/Hub'))
const CreditsAndPaymentPage = loadable(() => import(/* webpackChunkName: "CreditsPage" */ 'components/Pages/CreditsPage/CreditsAndPaymentPage'))
const FlightsSearchMultiCityResultsPage = loadable(() => import(/* webpackChunkName: "FlightsSearchMultiCityResultsPage" */ 'components/Flights/FlightsSearchMultiCityResultsPage'))
const FlightsSearchResultsPage = loadable(() => import(/* webpackChunkName: "FlightsSearchResultsPage" */ 'components/Flights/FlightsSearchResultsPage'))
const GoogleHotelSearchPage = loadable(() => import(/* webpackChunkName: "GoogleHotelSearchPage" */ 'components/Pages/HotelSearchPage/GoogleHotelSearchPage'))
const HotelSearchMapPage = loadable(() => import(/* webpackChunkName: "HotelSearchMapPage" */ 'components/Pages/HotelSearchPage/HotelSearchMapPage'))
const HotelSearchPage = loadable(() => import(/* webpackChunkName: "HotelSearchPage" */ 'components/Pages/HotelSearchPage/HotelSearchPage'))
const LegalPages = loadable(() => import(/* webpackChunkName: "LegalPages" */ 'components/Pages/LegalPages'))
const FeeSchedulePage = loadable(() => import(/* webpackChunkName: "FeeSchedulePage" */ 'components/Pages/FeeSchedulePage'))
const MyEscapesPage = loadable(() => import(/* webpackChunkName: "MyEscapesPaginatedPage" */ 'components/Pages/MyEscapesPage'))
const OfferPage = loadable(() => import(/* webpackChunkName: "OfferPage" */ 'components/OfferPage'))
const OrderPage = loadable(() => import(/* webpackChunkName: "OrderPage" */ 'components/Pages/MyEscapesPage/OrderPage'))
const ResetPasswordPage = loadable(() => import(/* webpackChunkName: "ResetPasswordPage" */ 'components/Pages/ResetPasswordPage/ResetPasswordPage'))
const SkyCheckConfirmationPage = loadable(() => import(/* webpackChunkName: "SkyCheckConfirmationPage" */ 'components/Pages/EmailConfirmedPage/SkyCheckConfirmation/SkyCheckConfirmationPage'))
const StaticPage = loadable(() => import(/* webpackChunkName: "StaticPage" */ 'components/Pages/StaticPage'))
const TourTravellerInfoPage = loadable(() => import(/* webpackChunkName: "TourTravellerInfoPage" */ 'components/Pages/TourTravellerInfo/TourTravellerInfoPage'))
const TravelCompanionsPage = loadable(() => import(/* webpackChunkName: "TravelCompanionPage" */ 'components/Pages/TravelCompanionsPage'))
const TravelPreferencesPage = loadable(() => import(/* webpackChunkName: "TravelPreferencesPage" */ 'components/Pages/AccountTravelPreferencesPage/TravelPreferencesPage'))
const UpgradeRoomPage = loadable(() => import(/* webpackChunkName: "UpgradeRoomPage" */ 'components/Pages/MyEscapesPage/UpgradeRoomPage'))
const UserCartPage = loadable(() => import(/* webpackChunkName: "UserCartPage" */ 'components/Pages/UserCartPage/UserCartPage'))
const UserReviews = loadable(() => import(/* webpackChunkName: "UserReviews" */ 'components/Pages/UserReviews'))
const AncillaryPaymentPage = loadable(() => import(/* webpackChunkName: "AncillaryPaymentPage" */ 'checkout/Pages/AncillaryPaymentPage'))
const AncillaryChangeReservationPage = loadable(() => import(/* webpackChunkName: "ChangeReservationPage" */ 'checkout/Pages/AncillaryChangeReservationPage'))

const MyEscapesPaginatedPage = loadable(() => import(/* webpackChunkName: "MyEscapesPaginatedPage" */ 'businessTraveller/pages/my-bookings/MyEscapesPaginatedPage'))
const BusinessTravellerOrderPage = loadable(() => import(/* webpackChunkName: "OrderPage" */ 'businessTraveller/pages/my-bookings/order'))
const BusinessTravellerLandingPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerLandingPage" */ 'businessTraveller/pages/landing/BusinessTravellerLandingPage'))
const BusinessTravellerSalesLandingPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerSalesLandingPage" */ 'businessTraveller/pages/sales-landing/BusinessTravellerSalesLandingPage'))
const BusinessTravellerLoginPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerLoginPage" */ 'businessTraveller/pages/login/BusinessTravellerLoginPage'))
const BusinessTravellerBusinessSignupPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerBusinessSignupPage" */ 'businessTraveller/pages/business-signup/BusinessTravellerBusinessSignupPage'))
const BusinessTravellerBusinessSignupCompletePage = loadable(() => import(/* webpackChunkName: "BusinessTravellerBusinessSignupCompletePage" */ 'businessTraveller/pages/business-signup-complete/BusinessTravellerBusinessSignupCompletePage'))
const BusinessTravellerEmployeeSetupPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerEmployeeSetupPage" */ 'businessTraveller/pages/employee-setup/BusinessTravellerEmployeeSetupPage'))
const BusinessTravellerApproveOrderPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerApproveOrderPage" */ 'businessTraveller/pages/approve-order/BusinessTravellerApproveOrderPage'))
const BusinessTravellerFaqPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerFaqPage" */ 'businessTraveller/pages/faq/BusinessTravellerFaqPage'))
const BusinessTravellerExpressionOfInterestPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerExpressionOfInterestPage" */ 'businessTraveller/pages/expression-of-interest/ExpressionOfInterestPage'))
const BusinessTravellerMyApprovalsPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerMyApprovalsPage" */ 'businessTraveller/pages/approval-requests/BusinessTravellerMyApprovalsPage'))
const BusinessTravellerMyTripRequestsPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerMyTripRequestsPage" */ 'businessTraveller/pages/approval-requests/BusinessTravellerMyTripRequestsPage'))
const BusinessTravellerMyTripRequestDetailsPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerMyTripRequestDetailsPage" */ 'businessTraveller/pages/approval-requests/BusinessTravellerMyTripRequestDetailsPage'))
const BusinessTravellerMyApprovalsDetailsPage = loadable(() => import(/* webpackChunkName: "BusinessTravellerMyApprovalsDetailsPage" */ 'businessTraveller/pages/approval-requests/BusinessTravellerMyApprovalsDetailsPage'))

const SocieteCampaignPage = loadable(() => import(/* webpackChunkName: "SocieteCampaignPage" */ 'loyalty/pages/societeCampaign/SocieteCampaignPage'))

interface Props {
  currentRegionCode: string
  isLoggedIn: boolean
  isLebtOrderSchemaV2Enabled: boolean
  isLebtSocieteEnabled: boolean
}

function PageRoutes(props: Props) {
  const { isLoggedIn, currentRegionCode, isLebtOrderSchemaV2Enabled, isLebtSocieteEnabled } = props
  const { isApp } = useWhiteLabelAppConfig()

  return (
    <Switch>
      {/* LE Business Traveller dedicate login route */}
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_LOGIN_ROUTE}`}>
        {({ match }) => (
          isLoggedIn
            ? <Redirect to={`/${match?.params.regionCode}${BUSINESS_TRAVELLER_ROOT_ROUTE}`} />
            : <BusinessTravellerLoginPage />
        )}
      </Route>

      {isLoggedIn && <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_ROOT_ROUTE}`} component={BusinessTravellerLandingPage} />}
      {/* In the mobile app, a logged-out user landing on the dedicated login route */}
      {!isLoggedIn && isApp && <Redirect exact from={`/:regionCode${BUSINESS_TRAVELLER_ROOT_ROUTE}`} to={`/${currentRegionCode}${BUSINESS_TRAVELLER_LOGIN_ROUTE}`} />}
      {!isLoggedIn && <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_ROOT_ROUTE}`} component={BusinessTravellerSalesLandingPage} />}

      {/* Business & Employee Signup */}
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_BUSINESS_SIGNUP_ROUTE}`} component={BusinessTravellerBusinessSignupPage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_BUSINESS_SIGNUP_COMPLETE_ROUTE}`} component={BusinessTravellerBusinessSignupCompletePage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_EMPLOYEE_SETUP_ROUTE}`} component={BusinessTravellerEmployeeSetupPage} />

      {/* Content Pages */}
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_FAQ_ROUTE}`} component={BusinessTravellerFaqPage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_EXPRESSION_OF_INTEREST_ROUTE}`} component={BusinessTravellerExpressionOfInterestPage} />

      {/* Trip Requests */}
      <AuthRoute showPopup path={`/:regionCode${BUSINESS_TRAVELLER_APPROVE_ORDER_ROUTE}`} component={BusinessTravellerApproveOrderPage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_MY_APPROVALS_ROUTE}/:tab(pending|history)?`} component={BusinessTravellerMyApprovalsPage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_MY_APPROVALS_VIEW_ROUTE}/:tripRequestId`} component={BusinessTravellerMyApprovalsDetailsPage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_ROUTE}/:tab(active|history)?`} component={BusinessTravellerMyTripRequestsPage} />
      <Route exact path={`/:regionCode${BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_VIEW_ROUTE}/:tripRequestId`} component={BusinessTravellerMyTripRequestDetailsPage} />

      {/* Société (loyalty) landing page */}
      {isLebtSocieteEnabled && <AuthRoute exact path={`/:regionCode/${LOYALTY_LANDING_PAGE}`} component={SocieteCampaignPage} />}

      {/* Redirects */}
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/approve-order/:approveRequestId" to="/:regionCode/approve-order/:approveRequestId" />
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/business-signup" to="/:regionCode/business-signup" />
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/business-signup-complete" to="/:regionCode/business-signup-complete" />
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/employee-setup" to="/:regionCode/employee-setup" />
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/my-approvals" to="/:regionCode/my-approvals" />
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/my-trip-requests" to="/:regionCode/my-trip-requests" />
      <RedirectWithStatus code={301} from="/:regionCode/business-traveller/frequently-asked-questions" to="/:regionCode/frequently-asked-questions" />

      <RedirectWithStatus code={301} from="/:regionCode/business-traveller" to="/:regionCode" />
      <RedirectWithStatus code={301} from="/:regionCode/hotels" to="/:regionCode" />
      <RedirectWithStatus code={301} from="/:regionCode/flights" to="/:regionCode" />
      <RedirectWithStatus code={301} from="/:regionCode/car-hire" to="/:regionCode" />
      <RedirectWithStatus code={301} from="/:regionCode/about-us" to="/:regionCode" />

      {/* Offer pages */}
      <Route exact path="/:regionCode/offer/:offerId">
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </Route>
      <Route path="/:regionCode/offer/:offerSlug/:offerId">
        {({ match }) => <OfferPage key={match?.params.offerId} />}
      </Route>
      <Route path="/:regionCode/partner/:offerSlug/:offerId">
        {({ match }) => <BedBankOfferPage key={match?.params.offerId} />}
      </Route>

      {/* Search Pages */}
      <Route exact path="/:regionCode/search" component={HotelSearchPage} />
      <Route exact path="/:regionCode/metasearch/:propertyId" component={GoogleHotelSearchPage} />
      <Route exact path="/:regionCode/search/map" component={HotelSearchMapPage} />
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && <Route exact path="/:regionCode/search/car-hire" component={CarHireSearchPage} />}

      {/* Check if redirects still required */}
      <RedirectWithStatus code={301} from="/:regionCode/refund-policy" to="/:regionCode/booking-policies" />

      {/* Account pages */}
      <RedirectWithStatus code={301} from="/:regionCode/account/my-escapes/upgrade-room/:orderId" to="/:regionCode/account/my-bookings/upgrade-room/:orderId" />
      <AuthRoute showPopup exact path="/:regionCode/account/my-bookings/upgrade-room/:orderId" component={UpgradeRoomPage} />

      <AuthRoute exact path="/:regionCode/account" component={AccountPage} />

      <RedirectWithStatus code={301} exact from="/:regionCode/account/payment-cards" to="/:regionCode/account/credits-payments" />
      <AuthRoute exact path="/:regionCode/account/credits-payments" component={CreditsAndPaymentPage} />

      <RedirectWithStatus code={301} from="/:regionCode/account/my-escapes/order/:orderId" to="/:regionCode/account/my-bookings/order/:orderId" />
      {isLebtOrderSchemaV2Enabled && (
        <AuthRoute showPopup exact path="/:regionCode/account/my-bookings/order/:orderId" component={BusinessTravellerOrderPage} />
      )}
      {!isLebtOrderSchemaV2Enabled && (
        <AuthRoute showPopup exact path="/:regionCode/account/my-bookings/order/:orderId" component={OrderPage} />
      )}

      <AuthRoute exact path="/:regionCode/account/travel-preferences" component={TravelPreferencesPage} />
      <AuthRoute exact path="/:regionCode/account/travel-companions" component={TravelCompanionsPage} />

      {/* Apps are still pointing to /my-escapes, so redirect to the updated route */}
      <RedirectWithStatus code={301} from="/:regionCode/my-escapes/:modalType?" to="/:regionCode/account/my-bookings/:modalType?" />
      <RedirectWithStatus code={301} from="/:regionCode/account/my-escapes/:modalType?" to="/:regionCode/account/my-bookings/:modalType?" />

      {isLebtOrderSchemaV2Enabled && (
        <AuthRoute showPopup path="/:regionCode/account/my-bookings/:tab(upcoming|cancelled|past|active-trip-requests)?" component={MyEscapesPaginatedPage} />
      )}
      {!isLebtOrderSchemaV2Enabled && (
        <AuthRoute showPopup path="/:regionCode/account/my-bookings/:tab(upcoming|cancelled|past)?" component={MyEscapesPage} />
      )}
      <AuthRoute exact path="/:regionCode/account/details/traveller" component={AccountDetailsTravellerPage} />
      <AuthRoute exact path="/:regionCode/account/details" component={AccountDetailsPage} showPopup />
      <AuthRoute path="/:regionCode/booking-form/:orderId?" component={TourTravellerInfoPage} />
      <AuthRoute showPopup exact path="/:regionCode/review/:orderId?" component={UserReviews} />
      <Route exact path="/:regionCode/reset-password/:resetPasswordToken" component={ResetPasswordPage} />

      {/* Booking pages */}
      <AuthRoute showPopup path="/:regionCode/booking/confirmation" component={ConfirmationPage} />
      <AuthRoute showPopup path="/:regionCode/bb/booking/confirmation" component={ConfirmationPage} />
      <RedirectWithStatus code={301} from="/:regionCode/confirmation/:orderId" to="/:regionCode/booking/confirmation?orderId=:orderId" />

      <Route
        path="/:regionCode/sharethis/verify"
        component={(): null => {
          const mainUrl = `https://platform-api.sharethis.com/js/sharethis.js#property=${config.GOOGLE_APP_ID}`
          const script = document.createElement('script')
          script.setAttribute('id', 'sharethis-js')
          script.src = mainUrl
          script.async = true
          document.body.appendChild(script)
          return null
        }}
      />

      {/* Content hub pages */}
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <RedirectWithStatus code={301} from="/:regionCode/pages/:slug" to="/:regionCode/:slug.p1" />}
      {config.CONTENT_HUB_ENABLED && config.contentHub &&
        <Route exact path="/:regionCode/:slug.p1" component={ContentHub} />}

      {config.staticPages.map(sp => {
        if (!sp.url?.to) return null

        const content = (props: any) => {
          if (sp.type === 'bestPrice') {
            return <BestPricePage />
          } else if (sp.type === 'termsAndConditions') {
            return <LegalPages slug="terms-and-conditions" location={props.location} />
          } else if (sp.type === 'refundPolicy') {
            return <LegalPages slug="refund-policy" location={props.location} />
          } else if (sp.type === 'privacyPolicy') {
            return <LegalPages slug="privacy-policy" location={props.location} />
          } else if (sp.type === 'bookingPolicies') {
            return <LegalPages slug="booking-policies" location={props.location} />
          } else if (sp.type === 'feeSchedule') {
            return <FeeSchedulePage />
          } else if (sp.type === 'businessFaq') {
            return <BusinessTravellerFaqPage />
          } else {
            return <StaticPage type={sp.type} title={sp.text} />
          }
        }

        // use AuthRoute when requireAuth
        if (sp.requiresAuth) {
          return <AuthRoute
            exact
            key={sp.url.to}
            path={`/:regionCode${sp.url.to}`}
            render={content}
          />
        }

        return <Route
          exact
          key={sp.url.to}
          path={`/:regionCode${sp.url.to}`}
          render={content}
        />
      })}

      {/* Flights pages */}
      <Route path="/:regionCode/flights-search-results" component={FlightsSearchResultsPage} />
      <Route path="/:regionCode/flights-search-results-multi-city" component={FlightsSearchMultiCityResultsPage} />

      {/* Checkout pages */}
      <AuthRoute showPopup path="/:regionCode/checkout-restore-cart" component={CheckoutRestoreCartPage} />
      <AuthRoute showPopup path="/:regionCode/checkout-gateway" component={CheckoutGatewayPage} />
      <Route exact path="/:regionCode/checkout/:cartId/:step?" component={CheckoutPageRoutes} />
      <Route path="/:regionCode/checkout/:step?" component={CheckoutPageRoutes} />

      <AuthRoute
        showPopup
        path="/:regionCode/ancillary-payment/:orderId/:type(deposit-balance|instalment-balance|instalment|reserve-balance|custom-offer|payment-schedule-balance)"
        component={AncillaryPaymentPage}
      />
      <AuthRoute
        showPopup
        path="/:regionCode/ancillary-change-reservation/:orderId/:type(change-dates|change-package|tour-optional-experiences)/:itemId?"
        component={AncillaryChangeReservationPage}
      />

      {/* Cart pages */}
      <AuthRoute showPopup path="/:regionCode/cart" component={UserCartPage} />

      {config.SPOOFING_SEND_CONFIRMATION_EMAIL && <Route path="/:regionCode/skycheck" component={SkyCheckConfirmationPage} />}

      {/* Mobile app marketing url, redirect this to home page until we have one */}
      <RedirectWithStatus code={302} from="/:regionCode/app" to="/:regionCode/" />

      <Route>
        <HttpStatus code={404}>
          <Error404Page />
        </HttpStatus>
      </Route>
    </Switch>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currentRegionCode: state.geo.currentRegionCode,
    isLoggedIn: selectLoggedIn(state),
    isLebtOrderSchemaV2Enabled: isLebtOrderSchemaV2Enabled(state),
    isLebtSocieteEnabled: isLebtSocieteEnabled(state),
  }
}

export default connect(mapStateToProps)(PageRoutes)
